import { useEffect, useMemo, useState } from 'react'
import QRCode from 'qrcode'
import PageHeader from '@/components/PageHeader'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QrCode, Copy, Check, Smartphone } from 'lucide-react'
import api from '@/lib/api/client'

/* Snelle onboarding voor Android devices — mDNS werkt niet in Chrome op
   Android, dus deelnemers en admins moeten via het IP-adres verbinden. Deze
   pagina toont een grote QR-code van de huidige base-URL zodat een gsm/tablet
   direct kan scannen en een home-screen-shortcut kan maken. */
export default function AdminQrPage() {
  const [customUrl, setCustomUrl] = useState('')
  const [dataUri, setDataUri] = useState('')
  const [gekopieerd, setGekopieerd] = useState(false)
  const [lanIp, setLanIp] = useState(null)   // fysiek IP van de host

  // Backend detecteert het fysieke IPv4 (LANSERVER_BIND_IP uit .env) — dat is
  // het IP dat Android-clients moeten gebruiken, want mDNS werkt niet op Android.
  useEffect(() => {
    api.get('/admin/system/lan-ip')
      .then(r => setLanIp(r.data?.ip || null))
      .catch(() => setLanIp(null))
  }, [])

  // Twee URL-bronnen: het detected IP van de host (Android-vriendelijk) of de
  // huidige browser-URL (kan `lanserver.local` zijn, wat op Android faalt).
  const huidigeBase = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return `${window.location.protocol}//${window.location.host}`
  }, [])

  const ipBase = useMemo(() => {
    if (!lanIp || typeof window === 'undefined') return ''
    const p = window.location.protocol
    const port = window.location.port
    return `${p}//${lanIp}${port ? `:${port}` : ''}`
  }, [lanIp])

  // Standaard-doel = IP-based (voor Android), fallback op de huidige URL als
  // het backend-endpoint geen IP kent (bv. 0.0.0.0 fallback in .env).
  const standaardBase = ipBase || huidigeBase
  const doelUrl = customUrl.trim() || standaardBase

  useEffect(() => {
    if (!doelUrl) return
    QRCode.toDataURL(doelUrl, {
      width: 512,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then(setDataUri)
      .catch(() => setDataUri(''))
  }, [doelUrl])

  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(doelUrl)
      setGekopieerd(true)
      setTimeout(() => setGekopieerd(false), 1500)
    } catch { /* geen clipboard-permission */ }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={QrCode}
        title="QR-code voor tablet/gsm"
        eyebrow="Onboarding"
      />

      <div className="grid gap-6 md:grid-cols-[1fr_1.2fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-acid" /> URL
            </CardTitle>
            <CardDescription>
              De QR-code wijst standaard naar dezelfde URL als waar je nu op zit.
              Pas eventueel aan om een andere pagina te delen (bv. de kassa).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="qr-url">Doel-URL</Label>
              <Input id="qr-url"
                     value={customUrl}
                     onChange={e => setCustomUrl(e.target.value)}
                     placeholder={standaardBase} />
              <p className="text-xs text-muted-foreground">
                Leeg = standaard-doel ({standaardBase}). Volledig URL invullen met <code>https://</code>.
              </p>
            </div>

            {ipBase && (
              <div className="rounded-md border border-live/30 bg-live/5 p-3 text-xs space-y-1">
                <div className="pf-mono uppercase tracking-widest text-live">
                  Automatisch detected LAN-IP
                </div>
                <p>
                  Backend meldt het huidige host-IP <code>{lanIp}</code>. QR-code
                  gebruikt <strong>{ipBase}</strong> zodat Android-devices ook
                  kunnen scannen (mDNS werkt niet in Chrome op Android).
                </p>
                {huidigeBase !== ipBase && (
                  <p className="text-muted-foreground">
                    Je zit momenteel op <code>{huidigeBase}</code>, maar de QR
                    verwijst bewust naar het IP.
                  </p>
                )}
              </div>
            )}
            {!ipBase && (
              <div className="rounded-md border border-signal/30 bg-signal/5 p-3 text-xs">
                <div className="pf-mono uppercase tracking-widest text-signal mb-1">
                  Geen IP gedetecteerd
                </div>
                <p>
                  Backend kent geen fysiek IP (<code>LANSERVER_BIND_IP</code> is
                  leeg of 0.0.0.0). QR verwijst naar de URL waar je nu op zit
                  ({huidigeBase}) — kan op Android falen als dat een <code>.local</code>-adres is.
                </p>
              </div>
            )}

            <div className="rounded-md border border-border bg-card/60 p-3 space-y-2">
              <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Gegenereerde link
              </div>
              <div className="flex items-center gap-2">
                <code className="text-sm bg-muted/40 px-2 py-1 rounded flex-1 truncate">{doelUrl}</code>
                <Button size="sm" variant="outline" onClick={kopieer}
                        className="shrink-0">
                  {gekopieerd
                    ? <><Check className="h-3.5 w-3.5 mr-1" /> Gekopieerd</>
                    : <><Copy  className="h-3.5 w-3.5 mr-1" /> Kopieer</>}
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-acid/20 bg-acid/5 p-3 space-y-1">
              <div className="pf-mono text-[10px] uppercase tracking-widest text-acid">
                Handig voor Android
              </div>
              <p className="text-sm">
                Scan de code met je camera-app of QR-scanner. Open in Chrome →
                menu → <em>"Toevoegen aan startscherm"</em>. Krijgt een icoontje
                op je home screen dat de site opent alsof het een app is.
              </p>
              <p className="text-xs text-muted-foreground">
                mDNS (<code>lanserver.local</code>) werkt niet in Chrome op Android
                — daarom deze IP-based QR-code.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Scan</CardTitle>
            <CardDescription>
              Houd de camera van je gsm/tablet voor de code. Werkt op elke Android
              en iOS zonder extra app te installeren.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid place-items-center p-4">
              {dataUri
                ? <img src={dataUri} alt={`QR voor ${doelUrl}`}
                       className="w-full max-w-[420px] rounded-md border border-border bg-white p-4" />
                : <div className="pf-mono text-xs text-muted-foreground py-16">
                    QR-code wordt gegenereerd…
                  </div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
